/**
 * Task Administration — Phase 1 of the 4-phase OBS appointment.
 *
 * The clinician administers the observation tasks and takes notes in each
 * task's general observation (masterNotes) box. The Self-Report panel is also
 * available here for recording self-reported experiences from the patient.
 *
 * Navigation (mirrors History / SSI):
 *   Primary CTAs  — task ↔ task, last task → Self-Report
 *   Grey phase link — "Go to ADOS-Informed Coding" (mid-phase shortcut)
 *   End of phase  — Self-Report promotes "Go to ADOS-Informed Coding" to the
 *                   primary forward CTA (Self-Report first draft waits until
 *                   Review; this exit only regenerates if already generated)
 *
 * Sidebar: 13 tasks + Self-Report (Sensory hidden on this page).
 */

import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { LockClosedIcon, ChevronRightIcon } from '@heroicons/react/solid';
import Layout from '../../../../components/Layout';
import Breadcrumb from '../../../../components/Breadcrumb';
import AppointmentChecklistControls from '../../../../components/autismModular/AppointmentChecklistControls';
import PreviewModeBanner from '../../../../components/PreviewModeBanner';
import ObservationTaskSelector from '../components/ObservationTaskSelector';
import ObservationTaskPanelV2a from '../components/ObservationTaskPanelV2a';
import GeneralObservationsPanel from '../components/GeneralObservationsPanel';
import ModularStepFooter from '../../../../components/autismModular/ModularStepFooter';
import { useDragAutoScroll } from '../../../../hooks/useDragAutoScroll';
import { useModularSectionExitRegeneration } from '../../../../hooks/useModularSectionExitRegeneration';
import useSessionState from '../../../../hooks/useSessionState';
import { TriggerGenerateDocumentSection } from '../../../../utils/TriggerGenerateDocumentSection';
import { getAutismOBSStepRoutes } from '../../../../utils/autismObsRouting';
import {
  shouldGenerateSelfReportSection,
  hasSelfReportNoteContent,
  noteMoveTouchesSelfReport,
  selfReportedTaggingChanged,
  criteriaRelevantNotesChanged,
  SELF_REPORTED_TAG_ID,
  SELF_REPORT_DATA_KEY,
} from '../../../../utils/obsSelfReportGeneration';
import { hasAnyObservationCriterionDecision } from '../../../../utils/observationCriteriaCompletion';
import { observationTaskHasRecordedData } from '../../../../utils/observationTaskAdministration';
import { GENERAL_PANELS } from '../config/generalObservationsConfig';
import { collectObsTaggedNotes } from '../utils/collectObsTaggedNotes';
import {
  getObsTaskAdminNavKey,
  defaultObsTaskAdminNav,
  resolveObsTaskAdminNav,
} from '../utils/obsTaskAdminNavState';
import useObservationStageData from '../hooks/useObservationStageData';

/** Self-Report is the final within-phase step before ADOS-informed coding. */
const SELF_REPORT_PANEL_ID = 'self_reported';

/** Primary forward CTA — brand fill for stepping to the next task / phase. */
const TASK_ADMIN_PRIMARY_FORWARD_BTN =
  'flex items-center py-2 px-4 rounded-md shadow-sm transition-all duration-200 text-white bg-primary hover:bg-primary/90';

export default function TaskAdministration() {
  const {
    patientId,
    tasks,
    navigate,
    flushStageWrites,
    isPreviewMode,
    canEdit,
    isObsV2,
    reportContent,
    selectedTaskIds,
    allTaskData,
    adosData,
    criteriaEvidence,
    handleAddTask,
    handleRemoveTask,
    handleFieldChange,
    handleGeneralFieldChange,
    handleUpdateNoteCriteriaTags,
    noteMoveTargets,
    handleMoveNoteAcross,
    isAppointmentChecklistEnabled,
  } = useObservationStageData({ excludeSensoryPanel: true });

  const showChecklist = isAppointmentChecklistEnabled;

  // ── LLM generation: Self-Report section triggers ──────────────────────
  // Track whether Self-Report notes were edited (typed or moved in/out) this
  // visit. Managed exits flush + maybe regenerate; the unmount cleanup is the
  // safety net for unmanaged exits (breadcrumb, browser back).
  const [hasChanges, setHasChanges] = useState(false);

  // ── LLM generation: Criteria knock-on from Task Administration edits ────
  // The Criteria section is normally (re)generated when the clinician leaves
  // Criterion Mapping. But several edits made HERE also feed those paragraphs,
  // and if Criteria was already generated once, the clinician may re-tag a note
  // or flip a flag on this page and then leave WITHOUT passing back through
  // Criterion Mapping. So we also regenerate Criteria on this phase's managed
  // exit when a relevant input changed this visit.
  //
  // What dirties Criteria from here:
  //   • "Not administered" toggles (v1 + v2) — change which tasks feed the LLM.
  //   • (v2 only) master-note edits, note tags, and cross-box note moves — the
  //     tagged-notes redesign routes these straight into criterion paragraphs.
  //
  // `criteriaDirtyRef` records that such a change happened this visit;
  // `criteriaRegenHandledRef` stops the unmount net double-firing after a
  // managed exit has already handled it.
  const criteriaDirtyRef = useRef(false);
  const criteriaRegenHandledRef = useRef(false);

  // At least one Self-Report note (typed in the dedicated box OR tagged
  // self_reported on a task / ADOS item). Empty early phase-skips must not
  // generate a blank Self-Report paragraph.
  const hasSelfReportContent = useMemo(
    () => hasSelfReportNoteContent(allTaskData, adosData),
    [allTaskData, adosData],
  );

  // Notes tagged SR that still live on Task Admin / ADOS pages — shown on the
  // Self-Report page so clinicians can review the full Self-Report feed.
  // Exclude notes already stored in general_self_reported (those appear in the
  // editable box below, and listing them twice would look like duplicates).
  const { externalSelfReportTaskNotes, externalSelfReportAdosNotes } = useMemo(() => {
    if (!isObsV2) {
      return { externalSelfReportTaskNotes: [], externalSelfReportAdosNotes: [] };
    }
    const { taskNotes, adosNotes } = collectObsTaggedNotes({
      tagId: SELF_REPORTED_TAG_ID,
      selectedTaskIds,
      tasks,
      allTaskData,
      adosData,
    });
    return {
      externalSelfReportTaskNotes: taskNotes.filter(
        (n) => n?.sourceRef?.dataKey !== 'general_self_reported',
      ),
      externalSelfReportAdosNotes: adosNotes,
    };
  }, [isObsV2, selectedTaskIds, tasks, allTaskData, adosData]);

  const selfReportAlreadyGenerated = Boolean(
    reportContent?.observation?.stageSectionsGenerated?.includes('Self-Report'),
  );

  // Unmount / managed exit: Task Admin never owns the Self-Report first draft
  // (that fires with Criteria on the forward step into Review). From here we
  // only refresh an already-generated paragraph when this visit dirtied it.
  const shouldRegenerateSelfReport = shouldGenerateSelfReportSection({
    hasContent: hasSelfReportContent,
    hasChanges,
    isGenerated: selfReportAlreadyGenerated,
    allowInitialGeneration: false,
  });

  const { markRegenHandled } = useModularSectionExitRegeneration({
    patientId,
    stageKey: 'observation',
    sectionTitle: 'Self-Report',
    hasChanges,
    canEdit,
    reportContent,
    shouldRegenerate: shouldRegenerateSelfReport,
    // Belt-and-braces: even if shouldRegenerate were miscomputed, never draft
    // Self-Report for the first time on a breadcrumb / early phase-skip exit.
    suppressInitialGeneration: true,
  });

  // Self-Report regeneration on exit (never a first draft from this phase).
  // Accepts a shared `dataReady` flush promise so a managed exit can flush once
  // and drive both Self-Report and Criteria triggers off the same settled
  // writes. Calls markRegenHandled() so the unmount net skips the duplicate.
  const flushAndGenerateSelfReport = useCallback((sharedDataReady) => {
    markRegenHandled();
    const dataReady = sharedDataReady || flushStageWrites('observation');
    if (canEdit) {
      const stageReport = reportContent?.observation;
      const generated = stageReport?.stageSectionsGenerated || [];
      const isGenerated = generated.includes('Self-Report');
      if (shouldGenerateSelfReportSection({
        hasContent: hasSelfReportContent,
        hasChanges,
        isGenerated,
        allowInitialGeneration: false,
      })) {
        TriggerGenerateDocumentSection(
          patientId, 'Autism Behavioural Observation', 'Self-Report', [], dataReady,
        );
      }
    }
    return dataReady;
  }, [
    markRegenHandled, flushStageWrites, canEdit, reportContent,
    hasChanges, hasSelfReportContent, patientId,
  ]);

  // Regenerate the Criteria section when a change made this visit would alter
  // its inputs. Only fires when Criteria was ALREADY generated (first
  // generation is owned by Criterion Mapping / Review) and at least one
  // decision still exists — never draft an empty paragraph. `criteriaEvidence`
  // is passed so v2 decisions count; v1 falls back to per-task decisions
  // (empty map -> legacy path). Marks the regen handled so the unmount net
  // does not repeat it.
  const maybeRegenerateCriteria = useCallback((dataReady) => {
    if (!canEdit) return;
    if (!criteriaDirtyRef.current) return;
    const generated = reportContent?.observation?.stageSectionsGenerated || [];
    if (!generated.includes('Criteria')) return;
    if (!hasAnyObservationCriterionDecision(
      selectedTaskIds, tasks, allTaskData, criteriaEvidence,
    )) return;
    criteriaRegenHandledRef.current = true;
    TriggerGenerateDocumentSection(
      patientId, 'Autism Behavioural Observation', 'Criteria', [], dataReady,
    );
  }, [canEdit, reportContent, selectedTaskIds, tasks, allTaskData, criteriaEvidence, patientId]);

  // Unmanaged-exit net for the Criteria knock-on (breadcrumb, browser back).
  // Kept as a plain effect rather than a second exit-regeneration hook, which
  // would clash with the Self-Report registration on the shared navigation
  // guard. A ref mirror keeps the cleanup reading current values.
  const criteriaExitRef = useRef(maybeRegenerateCriteria);
  criteriaExitRef.current = maybeRegenerateCriteria;
  useEffect(() => () => {
    if (criteriaRegenHandledRef.current) return;
    criteriaExitRef.current(undefined);
  }, []);

  // ── Session-only navigation state ──────────────────────────────────────
  // Persist which task / Self-Report panel is on screen across a full page
  // refresh for this patient. Clinical data and dirty/regen flags are NEVER
  // stored here — restoring a tab must not write to Firestore or fire AI
  // generation. See obsTaskAdminNavState.js for the full rationale.
  const [nav, setNav] = useSessionState(
    getObsTaskAdminNavKey(patientId),
    () => defaultObsTaskAdminNav(selectedTaskIds),
  );
  const { activeTaskId, activeView, activeGeneralPanel } = nav;

  // When the live task list loads or changes (e.g. a task is removed), re-check
  // that the restored target still exists. Skip while the list is empty so a
  // brief pre-fetch render cannot wipe a valid session restore.
  useEffect(() => {
    if (!Array.isArray(selectedTaskIds) || selectedTaskIds.length === 0) return;
    setNav((prev) => {
      const resolved = resolveObsTaskAdminNav(prev, selectedTaskIds);
      if (
        resolved.activeTaskId === prev.activeTaskId
        && resolved.activeView === prev.activeView
        && resolved.activeGeneralPanel === prev.activeGeneralPanel
      ) {
        return prev;
      }
      return resolved;
    });
  }, [selectedTaskIds, setNav]);

  // Scrollable main content ref for drag-auto-scroll near edges
  const contentRef = useRef(null);
  useDragAutoScroll(contentRef);

  // ── Sidebar handlers ───────────────────────────────────────────────────
  const handleSelectTask = useCallback((taskId) => {
    setNav({
      activeTaskId: taskId,
      activeView: 'task',
      activeGeneralPanel: null,
    });
  }, [setNav]);

  const handleOpenPicker = useCallback(() => {
    setNav((prev) => ({
      ...prev,
      activeView: 'picker',
      activeGeneralPanel: null,
    }));
  }, [setNav]);

  const handleSelectGeneral = useCallback((panelId) => {
    setNav((prev) => ({
      ...prev,
      activeGeneralPanel: panelId,
      activeView: 'general',
    }));
  }, [setNav]);

  // Wrap handleAddTask to also switch to the task view
  const handleAddAndSelectTask = useCallback((taskId) => {
    handleAddTask(taskId);
    setNav({
      activeTaskId: taskId,
      activeView: 'task',
      activeGeneralPanel: null,
    });
  }, [handleAddTask, setNav]);

  // Wrap handleRemoveTask to also fix navigation state
  const handleRemoveAndFixNav = useCallback((taskId) => {
    handleRemoveTask(taskId);
    setNav((current) => {
      if (current.activeTaskId !== taskId) return current;
      const remaining = selectedTaskIds.filter((id) => id !== taskId);
      if (remaining.length > 0) {
        return {
          activeTaskId: remaining[0],
          activeView: 'task',
          activeGeneralPanel: null,
        };
      }
      return {
        activeTaskId: null,
        activeView: 'picker',
        activeGeneralPanel: null,
      };
    });
  }, [handleRemoveTask, selectedTaskIds, setNav]);

  // ── Field change wrappers (bind activeTaskId / activeGeneralPanel) ─────
  // In v2, master-note edits (text OR criteria tags) feed the criterion
  // paragraphs, so any masterNotes change dirties Criteria. If the edit also
  // added/removed a `self_reported` tag (detected by comparing the tagged-note
  // signature before and after), it changes the Self-Report paragraph too, so
  // we dirty that section as well. v1 keeps its untouched behaviour: masterNotes
  // there carry no tags, and the picker is hidden, so neither branch fires.
  const handleTaskFieldChange = useCallback((fieldId, value) => {
    if (isObsV2 && fieldId === 'masterNotes') {
      criteriaDirtyRef.current = true;
      const prevNotes = allTaskData[activeTaskId]?.masterNotes;
      if (selfReportedTaggingChanged(prevNotes, value)) {
        setHasChanges(true);
      }
    }
    handleFieldChange(activeTaskId, fieldId, value);
  }, [handleFieldChange, activeTaskId, isObsV2, allTaskData]);

  const handleGeneralFieldChangeWrapped = useCallback((fieldId, value) => {
    if (activeGeneralPanel === 'self_reported') {
      // Editing the dedicated Self-Report box always dirties Self-Report.
      setHasChanges(true);
      // Plain Self-Report notes (auto-tagged self_reported only) must NOT dirty
      // Criteria. Dual-tagged notes in that box still feed A1–B4 / Other.
      if (isObsV2 && fieldId === 'notes') {
        const prevNotes = allTaskData?.[SELF_REPORT_DATA_KEY]?.notes;
        if (criteriaRelevantNotesChanged(prevNotes, value)) {
          criteriaDirtyRef.current = true;
        }
      }
    } else if (isObsV2 && fieldId === 'notes') {
      // Other general panels (e.g. Sensory): notes can be tagged to DSM criteria.
      criteriaDirtyRef.current = true;
    }
    handleGeneralFieldChange(activeGeneralPanel, fieldId, value);
  }, [handleGeneralFieldChange, activeGeneralPanel, isObsV2, allTaskData]);

  // Moves into/out of Self-Report must dirty the section so exit regeneration
  // re-runs the LLM paragraph (including when the last note is moved out). In
  // v2 a move also changes the source label a note is grouped under (which the
  // criterion prompts read), so any move dirties Criteria too. Moving INTO
  // Self-Report also stamps the self_reported tag inside the shared move
  // handler (same auto-tag as notes typed directly on this page).
  const handleMoveNoteAcrossWrapped = useCallback((sourceId, noteId, targetId, insertAt, notePatch) => {
    if (noteMoveTouchesSelfReport(sourceId, targetId)) setHasChanges(true);
    if (isObsV2) criteriaDirtyRef.current = true;
    handleMoveNoteAcross(sourceId, noteId, targetId, insertAt, notePatch);
  }, [handleMoveNoteAcross, isObsV2]);

  // Re-tag from the Self-Report page's cross-tagged panels — same storage write
  // Criterion Mapping uses, so Task Admin / ADOS stay in sync. Dirty Self-Report
  // and Criteria independently based on which tag membership actually changed.
  const handleRetagNote = useCallback((sourceRef, noteId, criteriaTags) => {
    let prevNotes = [];
    if (sourceRef?.kind === 'ados') {
      const field = sourceRef.field || `ados_${sourceRef.dataKey}_notes`;
      prevNotes = Array.isArray(adosData?.[field]) ? adosData[field] : [];
    } else if (sourceRef?.kind === 'task') {
      const entry = allTaskData?.[sourceRef.dataKey]
        || allTaskData?.[Number(sourceRef.dataKey)]
        || {};
      prevNotes = Array.isArray(entry?.[sourceRef.field]) ? entry[sourceRef.field] : [];
    }
    const nextNotes = prevNotes.map((n) => (
      n && n.id === noteId ? { ...n, criteriaTags } : n
    ));
    if (selfReportedTaggingChanged(prevNotes, nextNotes)) {
      setHasChanges(true);
    }
    if (isObsV2 && criteriaRelevantNotesChanged(prevNotes, nextNotes)) {
      criteriaDirtyRef.current = true;
    }
    handleUpdateNoteCriteriaTags(sourceRef, noteId, criteriaTags);
  }, [handleUpdateNoteCriteriaTags, isObsV2, adosData, allTaskData]);

  // ── Derived values ─────────────────────────────────────────────────────
  const activeTask = tasks.find(t => t.id === activeTaskId);
  const activeTaskData = allTaskData[activeTaskId] || {};

  // ── "Not administered" toggle ──────────────────────────────────────────
  // The flag is a plain field write, so it inherits debounced persistence and
  // progress recomputation. Reversible: unmarking writes `false` and restores
  // the (never-deleted) notes and decisions. Marking a task that already holds
  // clinical content asks for confirmation first; unmarking never confirms and
  // an untouched task toggles in a single click.
  const [notAdminConfirmOpen, setNotAdminConfirmOpen] = useState(false);

  const writeNotAdministered = useCallback((taskId, value) => {
    criteriaDirtyRef.current = true;
    handleFieldChange(taskId, 'notAdministered', value);
  }, [handleFieldChange]);

  const handleToggleNotAdministered = useCallback((next) => {
    if (next && observationTaskHasRecordedData(activeTaskData)) {
      setNotAdminConfirmOpen(true);
      return;
    }
    writeNotAdministered(activeTaskId, next);
  }, [activeTaskData, activeTaskId, writeNotAdministered]);

  const confirmNotAdministered = useCallback(() => {
    writeNotAdministered(activeTaskId, true);
    setNotAdminConfirmOpen(false);
  }, [activeTaskId, writeNotAdministered]);

  const currentIndex = useMemo(
    () => (activeTaskId != null ? selectedTaskIds.indexOf(activeTaskId) : -1),
    [selectedTaskIds, activeTaskId],
  );
  const prevTaskId = currentIndex > 0 ? selectedTaskIds[currentIndex - 1] : null;
  const nextTaskId = currentIndex >= 0 && currentIndex < selectedTaskIds.length - 1
    ? selectedTaskIds[currentIndex + 1]
    : null;
  const prevTask = prevTaskId ? tasks.find(t => t.id === prevTaskId) : null;
  const nextTask = nextTaskId ? tasks.find(t => t.id === nextTaskId) : null;

  const activeGeneralTitle = activeGeneralPanel
    ? GENERAL_PANELS.find(p => p.id === activeGeneralPanel)?.title || null
    : null;

  const activeLabel = activeView === 'general'
    ? `General Observations${activeGeneralTitle ? ` — ${activeGeneralTitle}` : ''}`
    : activeView === 'picker'
      ? null
      : activeTask
        ? `Task ${currentIndex + 1}. ${activeTask.shortTitle}`
        : null;

  const breadcrumbTuples = [
    ['Patient Overview', `/patient/${patientId}/adult-autism`],
    ['Adult Autism Assessment'],
    ['Behavioural Observation'],
    ['Task Administration'],
    ...(activeLabel ? [[activeLabel]] : []),
  ];

  // ── Navigate to ADOS-informed coding (next appointment phase) ──────────
  // Managed exit: flush writes + trigger Self-Report generation first.
  const handleProceedToAdos = useCallback(async () => {
    criteriaRegenHandledRef.current = true;
    const dataReady = flushStageWrites('observation');
    flushAndGenerateSelfReport(dataReady);
    maybeRegenerateCriteria(dataReady);
    await dataReady;
    navigate(getAutismOBSStepRoutes(patientId).adosCoding);
  }, [
    flushStageWrites, flushAndGenerateSelfReport,
    maybeRegenerateCriteria, navigate, patientId,
  ]);

  const handleGoToSelfReport = useCallback(() => {
    handleSelectGeneral(SELF_REPORT_PANEL_ID);
  }, [handleSelectGeneral]);

  // End-of-phase primary CTA (Self-Report → ADOS), same role as History → SSI.
  const adosForwardAction = (
    <button
      type="button"
      onClick={handleProceedToAdos}
      className={TASK_ADMIN_PRIMARY_FORWARD_BTN}
    >
      <span className="font-semibold text-sm">Go to ADOS-Informed Coding</span>
      <ChevronRightIcon className="w-5 h-5 ml-2" aria-hidden="true" />
    </button>
  );

  const selfReportForwardAction = (
    <button
      type="button"
      onClick={handleGoToSelfReport}
      className={TASK_ADMIN_PRIMARY_FORWARD_BTN}
    >
      <span className="font-semibold text-sm">Go to Self-Report</span>
      <ChevronRightIcon className="w-5 h-5 ml-2" aria-hidden="true" />
    </button>
  );

  // Mid-phase: primary Next task only; ADOS jump is a grey phaseForwardLink.
  // Last task: primary CTA becomes Self-Report (still within this phase).
  const taskFooterForwardActions = nextTask ? (
    <button
      type="button"
      onClick={() => handleSelectTask(nextTaskId)}
      className={TASK_ADMIN_PRIMARY_FORWARD_BTN}
    >
      <span className="font-semibold text-sm">{nextTask.shortTitle}</span>
      <ChevronRightIcon className="w-5 h-5 ml-2" aria-hidden="true" />
    </button>
  ) : selfReportForwardAction;

  // ── Render ─────────────────────────────────────────────────────────────
  return (
    <Layout activePage="dashboard" scrollable={false}>
      <div className="flex flex-col h-full min-h-0">
      <div className="flex-shrink-0">
        <Breadcrumb
          tupleList={breadcrumbTuples}
          allowWrap={showChecklist}
          rightSlot={showChecklist
            ? <AppointmentChecklistControls appointmentType="observation" />
            : undefined
          }
        />
      </div>

      <div className="flex flex-1 min-h-0">
        {/* Left sidebar — the bespoke OBS task selector */}
        <ObservationTaskSelector
          tasks={tasks}
          selectedTaskIds={selectedTaskIds}
          activeTaskId={activeTaskId}
          activeView={activeView}
          assessmentMode="preset"
          allTaskData={allTaskData}
          activeGeneralPanel={activeGeneralPanel}
          onSelectTask={handleSelectTask}
          onRemoveTask={handleRemoveAndFixNav}
          onOpenPicker={handleOpenPicker}
          onSelectGeneral={handleSelectGeneral}
          showReview={false}
          hideSensoryPanel
          criterionMappingMode={false}
          onToggleCriterionMapping={null}
          adosMode={false}
          onToggleAdosMode={null}
          showAdosSidebar={false}
          // v2 only: colour tasks green as soon as notes are captured (evidence
          // decisions come later in Criterion Mapping). v1 keeps evidence-based
          // colouring.
          useNotesCaptureSignal={isObsV2}
        />

        {/* Main panel */}
        <div className="flex-1 min-w-0 flex flex-col">
          {isPreviewMode && <PreviewModeBanner />}
          <div ref={contentRef} className="flex-1 min-h-0 overflow-y-auto">
            {activeView === 'picker' ? (
              <TaskPicker
                tasks={tasks}
                selectedTaskIds={selectedTaskIds}
                onAddTask={handleAddAndSelectTask}
              />
            ) : activeView === 'general' && activeGeneralPanel ? (
              <div className="p-6">
                <GeneralObservationsPanel
                  panelId={activeGeneralPanel}
                  data={allTaskData[`general_${activeGeneralPanel}`] || {}}
                  onFieldChange={handleGeneralFieldChangeWrapped}
                  moveTargets={noteMoveTargets}
                  onMoveNote={handleMoveNoteAcrossWrapped}
                  readOnly={!canEdit}
                  // Tag-to-criterion is a v2-only affordance; v1 patients keep
                  // plain notes (their Criterion Mapping never reads criteriaTags).
                  taggingEnabled={isObsV2}
                  externalTaggedTaskNotes={
                    activeGeneralPanel === SELF_REPORT_PANEL_ID
                      ? externalSelfReportTaskNotes
                      : null
                  }
                  externalTaggedAdosNotes={
                    activeGeneralPanel === SELF_REPORT_PANEL_ID
                      ? externalSelfReportAdosNotes
                      : null
                  }
                  onUpdateNoteTags={
                    activeGeneralPanel === SELF_REPORT_PANEL_ID && isObsV2
                      ? handleRetagNote
                      : null
                  }
                />
              </div>
            ) : activeTask ? (
              <ObservationTaskPanelV2a
                task={activeTask}
                taskData={activeTaskData}
                onFieldChange={handleTaskFieldChange}
                selectedTaskIds={selectedTaskIds}
                showObservations={false}
                sideBySideLayout
                criterionMappingMode={false}
                moveTargets={noteMoveTargets}
                onMoveNote={handleMoveNoteAcrossWrapped}
                readOnly={!canEdit}
                onToggleNotAdministered={canEdit ? handleToggleNotAdministered : null}
                // v2 only: master-note tag pickers feed the tagged-notes pipeline.
                taggingEnabled={isObsV2}
              />
            ) : null}
          </div>

          {/* Footer: per-task prev/next; grey phase link to ADOS; last → Self-Report */}
          {activeView === 'task' && activeTask && (
            <ModularStepFooter
              showBack={!!prevTask}
              backLabel={prevTask?.shortTitle}
              onBack={prevTask ? () => handleSelectTask(prevTaskId) : undefined}
              counterText={`Task ${currentIndex + 1} of ${selectedTaskIds.length}`}
              rightActions={taskFooterForwardActions}
              showNext={false}
              // Grey phase skip — available on every task, including the last
              // (where the primary CTA is Self-Report, still within this phase).
              phaseForwardLink={{
                label: 'Go to ADOS-Informed Coding',
                onClick: handleProceedToAdos,
              }}
            />
          )}

          {/* Self-Report — end of phase: ADOS jump is the primary forward CTA */}
          {activeView === 'general' && activeGeneralPanel === SELF_REPORT_PANEL_ID && (
            <ModularStepFooter
              showBack={selectedTaskIds.length > 0}
              backLabel={selectedTaskIds.length > 0 ? tasks.find(t => t.id === selectedTaskIds[selectedTaskIds.length - 1])?.shortTitle : undefined}
              onBack={selectedTaskIds.length > 0 ? () => handleSelectTask(selectedTaskIds[selectedTaskIds.length - 1]) : undefined}
              rightActions={adosForwardAction}
              showNext={false}
            />
          )}
        </div>
      </div>

      {/* Confirm before excluding a task that already holds recorded content.
          Same lightweight modal pattern as the Criterion Mapping soft gate. */}
      {notAdminConfirmOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setNotAdminConfirmOpen(false)}
          />
          <div className="relative bg-white rounded-xl shadow-2xl p-6 w-[520px] max-w-[90vw] text-left">
            <button
              type="button"
              onClick={() => setNotAdminConfirmOpen(false)}
              className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 text-xl leading-none"
              aria-label="Close"
            >
              &times;
            </button>
            <h3 className="text-lg font-bold text-gray-900 mb-2">
              Mark this task as not administered?
            </h3>
            <p className="text-sm text-gray-600 mb-5">
              This task already has recorded notes or evidence decisions. Marking it
              as not administered keeps that content but excludes the task from the
              criteria summary and generated report. You can reverse this at any time.
            </p>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setNotAdminConfirmOpen(false)}
                className="px-4 py-2 rounded-md text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors"
              >
                Keep task
              </button>
              <button
                type="button"
                onClick={confirmNotAdministered}
                className="px-4 py-2 rounded-md text-sm font-semibold text-white bg-primary hover:bg-primary/90 transition-colors"
              >
                Mark not administered
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </Layout>
  );
}


// ── TaskPicker (inline — same UI as the original) ────────────────────────────

function TaskPicker({ tasks, selectedTaskIds, onAddTask }) {
  return (
    <div className="flex flex-col items-center justify-center min-h-full px-8 py-12">
      <div className="text-center mb-10">
        <div className="w-16 h-16 bg-primary/5 rounded-2xl flex items-center justify-center mx-auto mb-5">
          <svg className="w-8 h-8 text-primary/50" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
        </div>
        <h2 className="text-gray-900 text-xl font-bold mb-2">
          {selectedTaskIds.length === 0
            ? 'Select your first observation task'
            : 'Add another observation task'}
        </h2>
        <p className="text-gray-500 text-sm max-w-lg">
          Choose tasks to include in this assessment. You can add or remove
          tasks at any time as the session progresses.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3 w-full max-w-2xl">
        {tasks.map((task) => {
          const isAlreadyAdded = selectedTaskIds.includes(task.id);
          const isAvailable = task.available;
          const isDisabled = isAlreadyAdded || !isAvailable;

          return (
            <button
              key={task.id}
              onClick={() => { if (!isDisabled) onAddTask(task.id); }}
              disabled={isDisabled}
              className={`
                relative flex items-center gap-3 px-4 py-3.5 rounded-xl text-left
                border transition-all duration-200 group
                ${isAlreadyAdded
                  ? 'bg-primary/5 border-primary/20 cursor-default'
                  : !isAvailable
                    ? 'bg-gray-50 border-gray-200 cursor-not-allowed opacity-60'
                    : 'bg-white border-gray-200 hover:border-primary hover:shadow-md hover:shadow-primary/5 cursor-pointer'
                }
              `}
            >
              <div className={`
                w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 text-sm
                transition-colors duration-200
                ${isAlreadyAdded
                  ? 'bg-primary/10 text-primary'
                  : !isAvailable
                    ? 'bg-gray-100 text-gray-400'
                    : 'bg-gray-100 text-gray-400 group-hover:bg-primary/10 group-hover:text-primary'
                }
              `}>
                {isAlreadyAdded ? (
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                ) : !isAvailable ? (
                  <LockClosedIcon className="w-3.5 h-3.5" />
                ) : (
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                  </svg>
                )}
              </div>

              <div className="flex-1 min-w-0">
                <p className={`text-sm font-medium leading-tight truncate ${isAlreadyAdded ? 'text-primary' : !isAvailable ? 'text-gray-400' : 'text-gray-700'}`}>
                  {task.shortTitle}
                </p>
                {!isAvailable && (
                  <p className="text-[10px] text-gray-400 mt-0.5">Coming soon</p>
                )}
                {isAlreadyAdded && (
                  <p className="text-[10px] text-primary/60 mt-0.5">
                    Task {selectedTaskIds.indexOf(task.id) + 1} in your assessment
                  </p>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
